'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface MapRegion {
  id: number;
  name: string;
  cities: string[];
  coordinates: string;
}

interface InteractiveMapProps {
  regions: MapRegion[];
  onRegionSelect: (regionId: number) => void;
}

export default function InteractiveMap({ regions, onRegionSelect }: InteractiveMapProps) {
  const [selectedRegion, setSelectedRegion] = useState<number | null>(null);
  const [hoveredRegion, setHoveredRegion] = useState<number | null>(null);

  const handleRegionClick = (regionId: number) => {
    setSelectedRegion(regionId);
    onRegionSelect(regionId);
  };

  return (
    <div className="bg-card rounded-lg shadow-brand p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-headline font-bold text-xl text-primary">
          Interactive Service Map
        </h3>
        <div className="flex items-center space-x-2 text-sm text-muted-foreground font-body">
          <Icon name="MapIcon" size={20} className="text-secondary" />
          <span>Click regions to explore</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Map Visualization */}
        <div className="relative bg-muted rounded-lg p-8 min-h-[400px] flex items-center justify-center">
          <div className="w-full max-w-md">
            <svg viewBox="0 0 400 400" className="w-full h-auto">
              {/* North Phoenix */}
              <path
                d="M 200 50 L 300 100 L 280 180 L 200 150 Z"
                fill={selectedRegion === 1 || hoveredRegion === 1 ? '#FF6B35' : '#2C5F41'}
                opacity={selectedRegion === 1 || hoveredRegion === 1 ? '0.8' : '0.6'}
                className="cursor-pointer transition-all duration-300 hover:opacity-80"
                onClick={() => handleRegionClick(1)}
                onMouseEnter={() => setHoveredRegion(1)}
                onMouseLeave={() => setHoveredRegion(null)}
              />
              {/* Central Phoenix */}
              <path
                d="M 200 150 L 280 180 L 260 260 L 180 240 Z"
                fill={selectedRegion === 2 || hoveredRegion === 2 ? '#FF6B35' : '#2C5F41'}
                opacity={selectedRegion === 2 || hoveredRegion === 2 ? '0.8' : '0.6'}
                className="cursor-pointer transition-all duration-300 hover:opacity-80"
                onClick={() => handleRegionClick(2)}
                onMouseEnter={() => setHoveredRegion(2)}
                onMouseLeave={() => setHoveredRegion(null)}
              />
              {/* East Valley */}
              <path
                d="M 260 260 L 350 280 L 340 350 L 250 330 Z"
                fill={selectedRegion === 3 || hoveredRegion === 3 ? '#FF6B35' : '#2C5F41'}
                opacity={selectedRegion === 3 || hoveredRegion === 3 ? '0.8' : '0.6'}
                className="cursor-pointer transition-all duration-300 hover:opacity-80"
                onClick={() => handleRegionClick(3)}
                onMouseEnter={() => setHoveredRegion(3)}
                onMouseLeave={() => setHoveredRegion(null)}
              />
              {/* West Valley */}
              <path
                d="M 100 180 L 180 240 L 160 320 L 80 280 Z"
                fill={selectedRegion === 4 || hoveredRegion === 4 ? '#FF6B35' : '#2C5F41'}
                opacity={selectedRegion === 4 || hoveredRegion === 4 ? '0.8' : '0.6'}
                className="cursor-pointer transition-all duration-300 hover:opacity-80"
                onClick={() => handleRegionClick(4)}
                onMouseEnter={() => setHoveredRegion(4)}
                onMouseLeave={() => setHoveredRegion(null)}
              />
              
              {/* Region Labels */}
              <text x="240" y="120" fill="white" fontSize="14" fontWeight="600" textAnchor="middle">North</text>
              <text x="220" y="210" fill="white" fontSize="14" fontWeight="600" textAnchor="middle">Central</text>
              <text x="295" y="310" fill="white" fontSize="14" fontWeight="600" textAnchor="middle">East</text>
              <text x="120" y="250" fill="white" fontSize="14" fontWeight="600" textAnchor="middle">West</text>
            </svg>
          </div>
        </div>

        {/* Region Details */}
        <div className="space-y-4">
          {regions.map((region) => (
            <button
              key={region.id}
              onClick={() => handleRegionClick(region.id)}
              onMouseEnter={() => setHoveredRegion(region.id)}
              onMouseLeave={() => setHoveredRegion(null)}
              className={`w-full text-left p-4 rounded-lg border-2 transition-brand ${
                selectedRegion === region.id
                  ? 'border-accent bg-accent/10' :'border-border bg-background hover:border-primary'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-headline font-bold text-base text-primary mb-2">
                    {region.name}
                  </h4>
                  <p className="text-sm text-muted-foreground font-body mb-2">
                    {region.cities.length} cities served
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {region.cities.slice(0, 3).map((city, index) => (
                      <span
                        key={index}
                        className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded font-body"
                      >
                        {city}
                      </span>
                    ))}
                    {region.cities.length > 3 && (
                      <span className="text-xs text-muted-foreground px-2 py-1 font-body">
                        +{region.cities.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
                <Icon
                  name="ChevronRightIcon"
                  size={20}
                  className={`text-primary transition-transform ${
                    selectedRegion === region.id ? 'rotate-90' : ''
                  }`}
                />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}